import { toScopeInput, type ScopeSelection } from './scope-selection';
import type { Role, RoleAssignmentScopeInput, Uuid } from '@/lib/api/types';

/*
  The state of a grant form, and the two conversions it needs. No React, so GrantForm.tsx can be
  hot reloaded and so these two rules can be read without the markup around them.
*/

export interface GrantFormState {
  readonly roleId: Uuid | null;
  readonly scope: ScopeSelection;
  /** A datetime-local input value, or the empty string for no expiry. */
  readonly expiresAtLocal: string;
}

/**
 * Converts a datetime-local value into an ISO instant, or null when empty.
 *
 * datetime-local carries no timezone, so Date.parse reads it in the browser's zone, which is what
 * the person meant when they typed it. Wire timestamps are UTC ISO strings per the rule at the top
 * of src/lib/api/types.ts, so the conversion happens here at the edge rather than by sending a
 * naive string and hoping the server picks the same zone. An assignment that expires at the wrong
 * hour is an elevation that outlives the night it was for.
 */
export function expiresAtToIso(local: string): string | null {
  if (local === '') {
    return null;
  }
  const ms = Date.parse(local);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

export interface ResolvedGrant {
  readonly role: Role;
  readonly scope: RoleAssignmentScopeInput;
}

/** The completed grant, or null while the form is still missing a role or a scope node. */
export function resolveGrant(
  state: GrantFormState,
  roles: readonly Role[],
): ResolvedGrant | null {
  if (state.roleId === null) {
    return null;
  }
  const role = roles.find((candidate) => candidate.id === state.roleId);
  const scope = toScopeInput(state.scope);
  if (role === undefined || scope === null) {
    return null;
  }
  return { role, scope };
}
